import React, {useEffect, useMemo, useState, useCallback} from 'react';
import {connect} from 'react-redux';
import {useQuery} from '@tanstack/react-query';
import {Autocomplete, Typography} from '@acuity-brands/fusion';
import {normalizeSelectOptions} from 'common/components/form/utils';
import {debounce} from 'lodash';

import './index.scss';
import PropTypes from 'prop-types';

const mapStateToProps = (state) => ({
  organization: state.settings.organization,
});
const groupBy = (option) => option.group.id;
export const SEARCH_DEBOUNCE_TIMEOUT_MS = 250;

const FusionSelectMultipleFilterAdapter = ({
  data,
  disabled,
  errors,
  field,
  handleChange,
  name,
  organization,
}) => {
  const [search, setSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  // used to store the selected option objects so we can continue
  //   displaying the relevant chips in the input when the current
  //   request doesn't include the selected options
  const [selectedOptions, setSelectedOptions] = useState([]);

  /**
   * @todo Make query pattern match other services.
   * @todo Get fetch URL from backend.
   */
  const org = organization.id;
  const queryParams = search ? `?search=${search}` : '';
  const url = `/errands/points/choices/${org}${queryParams}`;
  const {
    data: response,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['points', organization, search],
    queryFn: () => fetch(url).then((res) => res.json()),
    refetchOnWindowFocus: false,
  });

  const {data: items = []} = response || {};

  // since we know this control will request its own
  //   data, we can use the field's choices field
  //   to send over the preselected options
  useEffect(() => {
    // don't overwrite the selected options if they've already been set
    if (selectedOptions.length > 0) return;

    const preselectedOptions = normalizeSelectOptions(field.choices || []).map(
      (option) => ({
        ...option,
        group: {id: 'selected', displayName: 'Selected'},
      }),
    );
    setSelectedOptions(preselectedOptions);
  }, [field.choices]);

  const handleInputChange = useCallback(
    (_, inputValue, reason) => {
      if (reason === 'reset') return;
      setInputValue(inputValue);
    },
    [setInputValue],
  );
  const debounceSearch = useCallback(
    debounce((value) => {
      setSearch(value);
    }, SEARCH_DEBOUNCE_TIMEOUT_MS),
    [setSearch],
  );

  // allow user input to render immediately, but only
  //  trigger a request after a delay
  useEffect(() => {
    debounceSearch(inputValue);
  }, [inputValue, debounceSearch]);

  // the available options. listed in the dropdown.
  const options = useMemo(() => {
    if (!items) return [];

    const normalizedOptions = normalizeSelectOptions(
      items.sort((a, b) =>
        a.group.displayName.localeCompare(b.group.displayName),
      ),
    );

    return normalizedOptions;
  }, [items, selectedOptions]);

  // the selected options. represented by chips in the input.
  const value = useMemo(() => {
    if (isLoading) {
      return selectedOptions;
    }

    const availableOptions = (options || [])
      .concat(selectedOptions)
      .filter(Boolean);

    const selectedOptionIds = data || [];
    const chips = selectedOptionIds
      .map((id) =>
        availableOptions.find((option) => option.value === parseInt(id)),
      )
      .filter(Boolean);

    return chips;
  }, [data, options, selectedOptions, isLoading]);

  // create map of group IDs to group display names
  //   so we can show the group name in the dropdown
  const groups = useMemo(
    () =>
      options.reduce((acc, option) => {
        if (!acc.has(option.group.id)) {
          acc.set(option.group.id, option.group.displayName);
        }
        return acc;
      }, new Map()),
    [options],
  );

  return (
    <>
      <Autocomplete
        TextFieldProps={{
          label: field.label,
          error: errors && errors.length > 0,
          helperText: errors && errors.length > 0 ? errors[0] : null,
          errorTextPosition: 'left',
        }}
        disabled={disabled}
        filterOptions={(x) => x} // let backend filter
        groupBy={groupBy}
        id={name}
        inputValue={inputValue}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        loading={isFetching}
        multiple={true}
        onChange={(_, newValue) => {
          setSelectedOptions(newValue);
          const values = newValue.map((item) => item.value);
          handleChange(values);
        }}
        onInputChange={handleInputChange}
        options={options}
        renderGroup={({key, group, children}) => (
          <li key={key}>
            <div className={'group-header'}>{groups.get(group)}</div>
            <ul>{children}</ul>
          </li>
        )}
        useCheckbox={true}
        value={value}
      />
    </>
  );
};

FusionSelectMultipleFilterAdapter.propTypes = {
  data: PropTypes.any,
  disabled: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.string),
  field: PropTypes.shape({
    label: PropTypes.string, // maybe should be required
    choices: PropTypes.arrayOf(
      PropTypes.shape({
        display: PropTypes.string.isRequired,
        value: PropTypes.any.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  organization: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
};

const FusionSelectMultipleFilterAdapterWithState = connect(mapStateToProps)(
  FusionSelectMultipleFilterAdapter,
);

export default FusionSelectMultipleFilterAdapterWithState;
export {FusionSelectMultipleFilterAdapterWithState as FusionSelectMultipleFilterAdapter};
